import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { TrackBar } from '@digital-twins/deck-ui/components'
import { DECK_CHART_PRIMARY_COLOR } from '@digital-twins/deck-ui/foundations/chart-colors'
import { ChartLabel, ChartLabelFullText } from '@digital-twins/deck-ui/plots/_shared'

import {
  StudyFigureThemeCard,
  StudyFigureThemeCards,
  StudyFigureThemeVersion,
  StudyFigureThemeVersionCell,
} from '@digital-twins/shared-types'

/** The Summary stacked slot keeps its four-cell cap; Survey Data shows every bar. */
const MAX_VERSION_CELLS = 4

export interface ThemeCardFigureProps {
  /** The stamped block of the whole question (layout, versions, saturation, Other). */
  cards: StudyFigureThemeCards
  /** This theme's stamped figures. */
  card: StudyFigureThemeCard
  /** The card's position in the drawn list: every testid carries it. */
  index: number
  /** The first card carries the one overlap (i) of the question. */
  showsOverlapInfo: boolean
  /** The Other theme's title as its card prints it (the (i) names it); null: no Other card. */
  otherTitle?: string | null
  /** Survey Data uses magnitudes; the Summary stacked slot keeps its compact text figures. */
  displayBars?: boolean
}

interface VersionCellView {
  version: StudyFigureThemeVersion
  cell: StudyFigureThemeVersionCell | null
  label: string
}

/**
 * One theme card's stamped figure. Survey Data draws the service's shares as
 * TrackBars in the deck's primary chart token (the brand's first chart colour,
 * as choice bars use); the Summary stacked slot retains its text-only figures.
 * Bases, order and the Higher mark still come from Python.
 */
export function ThemeCardFigure(props: ThemeCardFigureProps) {
  const { cards, card, index, showsOverlapInfo, otherTitle = null, displayBars = false } = props

  const { t } = useTranslation('app')

  const overlapNote = [
    t('studyReport.questionCard.themeFigure.overlap'),
    cards.hasOther && otherTitle ? t('studyReport.questionCard.themeFigure.overlapOther', { other: otherTitle }) : null,
  ]
    .filter(Boolean)
    .join(' ')
  const overlapInfo = showsOverlapInfo ? (
    <ChartLabelFullText
      label=""
      index={0}
      description={overlapNote}
      testId="question-card-theme-overlap-info"
      ariaLabel={t('studyReport.questionCard.themeFigure.overlapLabel')}
    />
  ) : null

  const versionViews: Array<VersionCellView> = cards.versions.map((version, position) => ({
    version,
    cell: card.versions.find((cell) => cell.versionId === version.versionId) ?? null,
    label: version.label ?? t('studyReport.questionCard.laneFallbackTitle', { n: position + 1 }),
  }))
  const labelOf = (versionId: string | null) =>
    versionViews.find((view) => view.version.versionId === versionId)?.label ?? ''

  const formatPercent = (percent: number) => t('studyReport.questionCard.themeFigure.cellPercent', { percent })

  if (displayBars && cards.layout === 'pooled') {
    const percent = cards.pooledBaseState === 'too_few' ? null : card.percent
    const isTooFew = percent == null
    return (
      <div className="mt-3 space-y-2" data-testid={`question-card-theme-figure-${index}`}>
        {percent == null ? (
          <Text size="sm" color="muted" data-testid={`question-card-theme-figure-value-${index}`}>
            {t('studyReport.questionCard.themeFigure.tooFew')}
          </Text>
        ) : (
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0 flex-1" data-testid={`question-card-theme-bar-${index}`}>
              <TrackBar widthPct={percent} color={DECK_CHART_PRIMARY_COLOR} value={formatPercent(percent)} label="" withTooltip={false} />
            </div>
            <Text size="sm" className="shrink-0 font-semibold tabular-nums" data-testid={`question-card-theme-figure-value-${index}`}>
              {formatPercent(percent)}
            </Text>
          </div>
        )}
        {!isTooFew && cards.pooledBaseState === 'thin' && cards.pooledBase != null && (
          <Text size="xs" color="muted" className="tabular-nums" data-testid={`question-card-theme-figure-base-${index}`}>
            {t('studyReport.questionCard.themeFigure.basedOn', { count: cards.pooledBase })}
          </Text>
        )}
        {overlapInfo}
      </div>
    )
  }

  if (cards.layout === 'pooled') {
    const isTooFew = cards.pooledBaseState === 'too_few' || card.percent == null
    return (
      <div className="mt-3" data-testid={`question-card-theme-figure-${index}`}>
        <div className="flex items-center gap-1.5">
          <Text size="sm" color="muted" className="tabular-nums" data-testid={`question-card-theme-figure-value-${index}`}>
            {isTooFew
              ? t('studyReport.questionCard.themeFigure.tooFew')
              : t('studyReport.questionCard.themeFigure.mentionedBy', { percent: card.percent })}
          </Text>
          {overlapInfo}
        </div>
        {!isTooFew && cards.pooledBaseState === 'thin' && cards.pooledBase != null && (
          <Text size="xs" color="muted" className="tabular-nums" data-testid={`question-card-theme-figure-base-${index}`}>
            {t('studyReport.questionCard.themeFigure.basedOn', { count: cards.pooledBase })}
          </Text>
        )}
      </div>
    )
  }

  const hasOverflow = versionViews.length > MAX_VERSION_CELLS
  const shown = hasOverflow ? versionViews.slice(0, MAX_VERSION_CELLS - 1) : versionViews
  const hidden = hasOverflow ? versionViews.slice(MAX_VERSION_CELLS - 1) : []

  const renderCellValue = (view: VersionCellView) => {
    const { version, cell } = view
    const testIdBase = `question-card-theme-version-${index}-${version.versionId}`
    if (version.baseState === 'too_few' || cell?.percent == null) {
      return (
        <Text size="xs" color="muted" data-testid={`${testIdBase}-value`}>
          {t('studyReport.questionCard.themeFigure.tooFew')}
        </Text>
      )
    }
    const isMarked = card.higherVersionId === version.versionId
    return (
      <>
        <div className="flex flex-wrap items-center gap-1.5">
          <Text
            size="sm"
            className={cn('leading-tight tabular-nums', isMarked ? 'font-semibold text-foreground' : 'text-muted-foreground')}
            data-testid={`${testIdBase}-value`}
          >
            {formatPercent(cell.percent)}
          </Text>
          {isMarked && (
            <TooltipProvider>
              <Tooltip delayDuration={150}>
                <TooltipTrigger
                  type="button"
                  className="rounded-full border border-border px-1.5 text-xs leading-4 text-muted-foreground"
                  data-testid={`question-card-theme-higher-${index}`}
                >
                  {t('studyReport.questionCard.themeFigure.higher')}
                </TooltipTrigger>
                <TooltipContent data-testid={`question-card-theme-higher-${index}-content`}>
                  {t('studyReport.questionCard.themeFigure.higherHint', { label: labelOf(card.higherThanVersionId) })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {version.baseState === 'thin' && (
          <Text size="xs" color="muted" className="tabular-nums" data-testid={`${testIdBase}-base`}>
            {t('studyReport.questionCard.themeFigure.basedOn', { count: version.base })}
          </Text>
        )}
      </>
    )
  }

  if (displayBars) {
    return (
      <div className="mt-3 space-y-3" data-testid={`question-card-theme-figure-${index}`}>
        {versionViews.map((view) => {
          const { version, cell } = view
          const percent = version.baseState === 'too_few' ? null : cell?.percent ?? null
          return (
            <div key={version.versionId} className="grid min-w-0 grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)_auto] items-center gap-2 sm:grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] sm:gap-3" data-testid={`question-card-theme-version-${index}-${version.versionId}`}>
              <div className="flex min-w-0 items-center gap-1">
                <ChartLabel
                  text={view.label}
                  hintIndex={0}
                  maxLines={1}
                  className="min-w-0 text-xs leading-tight text-muted-foreground"
                  hintSize={11}
                  hintTestId={`question-card-theme-version-${index}-${version.versionId}-label-info`}
                  data-testid={`question-card-theme-version-${index}-${version.versionId}-label`}
                />
              </div>
              {percent != null ? (
                <div className="min-w-0" data-testid={`question-card-theme-bar-${index}-${version.versionId}`}>
                  <TrackBar widthPct={percent} color={DECK_CHART_PRIMARY_COLOR} value={formatPercent(percent)} label={view.label} withTooltip={false} />
                </div>
              ) : <span />}
              <div className="min-w-0">{renderCellValue(view)}</div>
            </div>
          )
        })}
        {overlapInfo}
      </div>
    )
  }

  const hiddenSummary = hidden.map((view) => {
    const value =
      view.version.baseState === 'too_few' || view.cell?.percent == null
        ? t('studyReport.questionCard.themeFigure.tooFew')
        : formatPercent(view.cell.percent)
    const line =
      card.higherVersionId === view.version.versionId
        ? t('studyReport.questionCard.themeFigure.moreVersionsLineHigher', {
            label: view.label,
            value,
            higher: t('studyReport.questionCard.themeFigure.higher'),
          })
        : t('studyReport.questionCard.themeFigure.moreVersionsLine', { label: view.label, value })
    return { versionId: view.version.versionId, line }
  })

  return (
    <div className="mt-3 flex items-start gap-2" data-testid={`question-card-theme-figure-${index}`}>
      <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        {shown.map((view) => (
          <div
            key={view.version.versionId}
            className="min-w-0"
            data-testid={`question-card-theme-version-${index}-${view.version.versionId}`}
          >
            <div className="flex min-w-0 items-center gap-1">
              <ChartLabel
                text={view.label}
                hintIndex={0}
                maxLines={1}
                className="min-w-0 text-xs leading-tight text-muted-foreground"
                hintSize={11}
                hintTestId={`question-card-theme-version-${index}-${view.version.versionId}-label-info`}
                data-testid={`question-card-theme-version-${index}-${view.version.versionId}-label`}
              />
            </div>
            {renderCellValue(view)}
          </div>
        ))}
        {hasOverflow && (
          <div className="min-w-0">
            <TooltipProvider>
              <Tooltip delayDuration={150}>
                <TooltipTrigger
                  type="button"
                  className="rounded-full border border-border px-2 text-xs leading-5 text-muted-foreground tabular-nums"
                  aria-label={t('studyReport.questionCard.themeFigure.moreVersionsLabel', { count: hidden.length })}
                  data-testid={`question-card-theme-versions-more-${index}`}
                >
                  {t('studyReport.questionCard.themeFigure.moreVersions', { count: hidden.length })}
                </TooltipTrigger>
                <TooltipContent data-testid={`question-card-theme-versions-more-${index}-content`}>
                  {hiddenSummary.map((entry) => (
                    <div key={entry.versionId}>{entry.line}</div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
      {overlapInfo}
    </div>
  )
}
